/**
 * Circuit Breaker for Indexer Requests
 *
 * Implements the circuit breaker pattern to prevent cascading failures
 * when the indexer is under load or unavailable.
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Failures exceeded threshold, requests are rejected immediately
 * - HALF_OPEN: Testing if service recovered, limited requests allowed
 */

import { CircuitBreakerOpenError } from './errors'

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN'

interface CircuitBreakerConfig {
  /** Number of failures before opening the circuit */
  failureThreshold: number
  /** Time in ms to wait before transitioning from OPEN to HALF_OPEN */
  resetTimeout: number
  /** Number of successful requests needed in HALF_OPEN to close the circuit */
  successThreshold: number
  /** Time window in ms to count failures */
  failureWindow: number
}

interface CircuitBreakerState {
  state: CircuitState
  failureCount: number
  successCount: number
  lastFailureTime: number
  nextResetTime: number
  failureTimestamps: number[]
}

const DEFAULT_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 5, // Open after 5 failures
  resetTimeout: 60000, // Wait 60 seconds before trying again
  successThreshold: 2, // Need 2 successes to close
  failureWindow: 30000, // Count failures within 30 seconds
}

/**
 * Circuit Breaker implementation for the indexer
 */
class CircuitBreaker {
  private state: CircuitBreakerState
  private config: CircuitBreakerConfig

  constructor(config: Partial<CircuitBreakerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.state = {
      state: 'CLOSED',
      failureCount: 0,
      successCount: 0,
      lastFailureTime: 0,
      nextResetTime: 0,
      failureTimestamps: [],
    }
  }

  /**
   * Check if a request can proceed
   * @throws CircuitBreakerOpenError if the circuit is open
   */
  canRequest(): boolean {
    this.updateState()

    if (this.state.state === 'OPEN') {
      throw new CircuitBreakerOpenError(this.state.nextResetTime, this.state.failureCount)
    }

    return true
  }

  /**
   * Record a successful request
   */
  recordSuccess(): void {
    this.state.successCount++

    if (this.state.state === 'HALF_OPEN') {
      if (this.state.successCount >= this.config.successThreshold) {
        this.close()
      }
    } else if (this.state.state === 'CLOSED') {
      // Reset failure count on success in closed state
      this.state.failureCount = 0
      this.state.failureTimestamps = []
    }
  }

  /**
   * Record a failed request
   */
  recordFailure(): void {
    const now = Date.now()
    this.state.lastFailureTime = now
    this.state.failureTimestamps.push(now)

    // Remove old failures outside the window
    this.state.failureTimestamps = this.state.failureTimestamps.filter(
      (ts) => now - ts < this.config.failureWindow,
    )

    this.state.failureCount = this.state.failureTimestamps.length

    if (this.state.state === 'HALF_OPEN') {
      // Any failure in HALF_OPEN reopens the circuit
      this.open()
    } else if (this.state.state === 'CLOSED') {
      if (this.state.failureCount >= this.config.failureThreshold) {
        this.open()
      }
    }
  }

  /**
   * Get current circuit state for monitoring
   */
  getState(): Readonly<CircuitBreakerState> {
    this.updateState()
    return { ...this.state }
  }

  /**
   * Manually reset the circuit breaker
   */
  reset(): void {
    this.state = {
      state: 'CLOSED',
      failureCount: 0,
      successCount: 0,
      lastFailureTime: 0,
      nextResetTime: 0,
      failureTimestamps: [],
    }
  }

  private updateState(): void {
    const now = Date.now()

    // Clean up old failure timestamps
    this.state.failureTimestamps = this.state.failureTimestamps.filter(
      (ts) => now - ts < this.config.failureWindow,
    )
    this.state.failureCount = this.state.failureTimestamps.length

    // Check if we should transition from OPEN to HALF_OPEN
    if (this.state.state === 'OPEN' && now >= this.state.nextResetTime) {
      this.halfOpen()
    }

    // Auto-close if in CLOSED state and failures cleared
    if (this.state.state === 'CLOSED' && this.state.failureCount === 0) {
      this.state.successCount = 0
    }
  }

  private open(): void {
    this.state.state = 'OPEN'
    this.state.nextResetTime = Date.now() + this.config.resetTimeout
    this.state.successCount = 0
    console.warn(
      `[CircuitBreaker] Circuit OPENED after ${this.state.failureCount} failures. ` +
        `Will retry after ${this.config.resetTimeout}ms`,
    )
  }

  private halfOpen(): void {
    this.state.state = 'HALF_OPEN'
    this.state.successCount = 0
    console.info('[CircuitBreaker] Circuit HALF_OPEN, testing recovery...')
  }

  private close(): void {
    this.state.state = 'CLOSED'
    this.state.failureCount = 0
    this.state.successCount = 0
    this.state.failureTimestamps = []
    console.info('[CircuitBreaker] Circuit CLOSED, service recovered')
  }
}

// Singleton instance for the indexer circuit breaker
let indexerCircuitBreaker: CircuitBreaker | null = null

/**
 * Get the indexer circuit breaker instance
 */
export function getIndexerCircuitBreaker(): CircuitBreaker {
  if (!indexerCircuitBreaker) {
    indexerCircuitBreaker = new CircuitBreaker({
      failureThreshold: 5,
      resetTimeout: 60000, // 1 minute
      successThreshold: 2,
      failureWindow: 30000, // 30 seconds
    })
  }
  return indexerCircuitBreaker
}

/**
 * Execute a function with circuit breaker protection
 */
export async function withCircuitBreaker<T>(fn: () => Promise<T>): Promise<T> {
  const breaker = getIndexerCircuitBreaker()

  // Check if we can make the request
  breaker.canRequest()

  try {
    const result = await fn()
    breaker.recordSuccess()
    return result
  } catch (error) {
    breaker.recordFailure()
    throw error
  }
}

export { CircuitBreaker }
export type { CircuitBreakerConfig, CircuitBreakerState, CircuitState }
